/*
 * Bitácora del ciclo: tareas, acuerdos y pruebas/re-pruebas (con hitos de
 * revisión y piezas vinculadas), portada desde el bitacora_action de
 * marketing.php a las mismas tablas (marketing_bitacora, _hitos, _piezas),
 * filtradas por el producto de este ciclo. No depende de que exista un
 * corte oficial. Operaciones ve todo en solo lectura.
 */
import React, { useCallback, useEffect, useState } from 'react'
import authAxios from '../../../../config/authAxios'

const TIPOS = [
  { value: 'tarea', label: 'Tarea' },
  { value: 'acuerdo', label: 'Acuerdo' },
  { value: 'prueba', label: 'Prueba' },
  { value: 're_prueba', label: 'Re prueba' },
]
const tipoLabel = value => TIPOS.find(t => t.value === value)?.label || value
const ESTADOS = ['por_iniciar', 'completado', 'archivado']
const estadoBadgeClass = value => `am-badge am-badge-${{ por_iniciar: 'sin_datos', completado: 'verde', archivado: 'amarillo' }[value] || 'sin_datos'}`

const emptyForm = { tipo: 'tarea', contenido: '', fecha: new Date().toISOString().slice(0, 10), campo_dato: '', campo_causa: '', campo_hipotesis: '', campo_resultado_esperado: '', campo_indicador: '', id_prueba_referencia: '' }

function PieceChips({ piezas, selected, onToggle }) {
  const [filtro, setFiltro] = useState('')
  const visibles = piezas.filter(p => !filtro || p.nombre.toLowerCase().includes(filtro.toLowerCase()))
  return <div>
    <input type="text" className="form-control form-control-sm mb-2" placeholder="Buscar pieza…" value={filtro} onChange={e => setFiltro(e.target.value)} />
    <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, padding: 8 }}>
      {visibles.length === 0 && <small className="text-muted">Sin piezas.</small>}
      {visibles.map(p => <label key={p.id_creative} className="d-flex align-items-center mb-1" style={{ gap: 6, fontSize: 12, fontWeight: 'normal' }}>
        <input type="checkbox" checked={selected.includes(p.id_creative)} onChange={() => onToggle(p.id_creative)} />
        {p.thumbnail_url && <img src={p.thumbnail_url} alt="" style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4 }} />}
        <span className="text-uppercase text-muted" style={{ fontSize: 9 }}>{p.plataforma}</span>
        {p.nombre}
      </label>)}
    </div>
  </div>
}

const BitacoraAgente = ({ campaignId, isMarketing, onNotice, onError }) => {
  const [entradas, setEntradas] = useState([])
  const [piezasDisponibles, setPiezasDisponibles] = useState([])
  const [loading, setLoading] = useState(false)
  const [verTodo, setVerTodo] = useState(false)
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [expandido, setExpandido] = useState(null)
  const [metricas, setMetricas] = useState(null)

  const load = useCallback(() => {
    if (!campaignId) return
    setLoading(true)
    authAxios.get(`/admin/agente-marketing/bitacora?id_camp=${campaignId}&todo=${verTodo ? 1 : 0}`)
      .then(r => { setEntradas(r.data?.data?.entradas || []); setPiezasDisponibles(r.data?.data?.piezas_disponibles || []) })
      .catch(e => onError(e.response?.data?.msg || 'No se pudo cargar la bitácora.'))
      .finally(() => setLoading(false))
  }, [campaignId, verTodo, onError])

  useEffect(() => { load() }, [load])

  const pruebasDisponibles = entradas.filter(e => ['prueba', 're_prueba'].includes(e.tipo))

  const submitEntry = async event => {
    event.preventDefault()
    setSaving(true)
    try {
      await authAxios.post('/admin/agente-marketing/bitacora', { ...form, id_camp: campaignId })
      onNotice('Entrada registrada.')
      setForm(emptyForm)
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo registrar la entrada.') }
    finally { setSaving(false) }
  }

  const changeState = async (entry, estado) => {
    try {
      await authAxios.post(`/admin/agente-marketing/bitacora/${entry.id}/estado`, { id_camp: campaignId, estado })
      onNotice('Estado actualizado.')
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo cambiar el estado.') }
  }

  const saveFinalResult = async entry => {
    const resultado = window.prompt('Resultado final:', entry.campo_resultado || '')
    if (resultado === null || !resultado.trim()) return
    const fecha = window.prompt('Fecha de revisión (YYYY-MM-DD), opcional:', entry.campo_fecha_revision || '') || ''
    try {
      await authAxios.post(`/admin/agente-marketing/bitacora/${entry.id}/resultado`, { id_camp: campaignId, campo_resultado: resultado.trim(), fecha_resultado: fecha.trim() })
      onNotice('Resultado guardado.')
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo guardar el resultado.') }
  }

  const addHito = async entry => {
    const fecha = window.prompt('Fecha del hito (YYYY-MM-DD):', new Date().toISOString().slice(0, 10))
    if (!fecha) return
    const descripcion = window.prompt('Descripción del hito:', '') || ''
    try {
      await authAxios.post('/admin/agente-marketing/bitacora/hitos', { id_camp: campaignId, id_bitacora: entry.id, fecha, descripcion })
      onNotice('Hito agregado.')
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo agregar el hito.') }
  }

  const saveHitoResult = async hito => {
    const resultado = window.prompt('Resultado del hito:', hito.resultado || '')
    if (resultado === null || !resultado.trim()) return
    try {
      await authAxios.post(`/admin/agente-marketing/bitacora/hitos/${hito.id}/resultado`, { id_camp: campaignId, resultado: resultado.trim() })
      onNotice('Resultado de hito guardado.')
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo guardar el resultado del hito.') }
  }

  const deleteHito = async hito => {
    if (!window.confirm('¿Eliminar este hito? Solo se puede si no tiene resultado.')) return
    try {
      await authAxios.post(`/admin/agente-marketing/bitacora/hitos/${hito.id}/eliminar`, { id_camp: campaignId })
      onNotice('Hito eliminado.')
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo eliminar el hito.') }
  }

  const savePieceLink = async (scope, refId, ids) => {
    try {
      await authAxios.post('/admin/agente-marketing/bitacora/piezas', { id_camp: campaignId, scope, id_ref: refId, piezas: ids.join(',') })
      onNotice('Piezas vinculadas.')
      await load()
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo vincular las piezas.') }
  }

  const verMetricas = async ids => {
    if (!ids.length) { onError('Selecciona al menos una pieza.'); return }
    try {
      const r = await authAxios.get(`/admin/agente-marketing/bitacora/metricas-piezas?id_camp=${campaignId}&piezas=${ids.join(',')}`)
      setMetricas(r.data?.data || null)
    } catch (e) { onError(e.response?.data?.msg || 'No se pudo calcular las métricas.') }
  }

  if (!campaignId) return null
  const entradasFiltradas = entradas.filter(e => filtroTipo === 'todos' || e.tipo === filtroTipo)

  return <div>
    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 8 }}>
      <div className="d-flex" style={{ gap: 8 }}>
        <select className="form-control form-control-sm" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="todos">Todos los tipos</option>
          {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label className="d-flex align-items-center" style={{ gap: 4, fontWeight: 'normal', fontSize: 13 }}>
          <input type="checkbox" checked={verTodo} onChange={e => setVerTodo(e.target.checked)} /> Ver todo el historial del producto
        </label>
      </div>
    </div>

    {isMarketing && <form onSubmit={submitEntry} className="am-detail mb-3">
      <h6>+ Nueva entrada</h6>
      <div className="form-row">
        <div className="form-group col-md-3">
          <label>Tipo</label>
          <select className="form-control" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value, id_prueba_referencia: '' })}>
            {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="form-group col-md-3">
          <label>Fecha</label>
          <input type="date" className="form-control" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} />
        </div>
        {form.tipo === 're_prueba' && <div className="form-group col-md-6">
          <label>Prueba original</label>
          <select className="form-control" required value={form.id_prueba_referencia} onChange={e => setForm({ ...form, id_prueba_referencia: e.target.value })}>
            <option value="">Seleccione</option>
            {pruebasDisponibles.map(p => <option key={p.id} value={p.id}>#{p.id} · {p.contenido.slice(0, 60)}</option>)}
          </select>
        </div>}
        <div className="form-group col-md-12">
          <label>{['prueba', 're_prueba'].includes(form.tipo) ? 'Hipótesis / acción' : 'Contenido'}</label>
          <textarea className="form-control" rows={2} required value={form.contenido} onChange={e => setForm({ ...form, contenido: e.target.value })} />
        </div>
        {['prueba', 're_prueba'].includes(form.tipo) && <>
          <div className="form-group col-md-6"><label>Dato</label><textarea className="form-control" rows={2} value={form.campo_dato} onChange={e => setForm({ ...form, campo_dato: e.target.value })} /></div>
          <div className="form-group col-md-6"><label>Causa</label><textarea className="form-control" rows={2} value={form.campo_causa} onChange={e => setForm({ ...form, campo_causa: e.target.value })} /></div>
          <div className="form-group col-md-6"><label>Resultado esperado</label><input type="text" className="form-control" value={form.campo_resultado_esperado} onChange={e => setForm({ ...form, campo_resultado_esperado: e.target.value })} /></div>
          <div className="form-group col-md-6"><label>Indicador</label><input type="text" className="form-control" value={form.campo_indicador} onChange={e => setForm({ ...form, campo_indicador: e.target.value })} /></div>
        </>}
      </div>
      <button className="btn btn-primary" disabled={saving}>{saving ? 'Guardando…' : 'Registrar'}</button>
    </form>}

    {entradasFiltradas.length === 0 && !loading && <p className="text-center text-muted">Sin entradas todavía.</p>}

    {entradasFiltradas.map(entry => {
      const isOpen = expandido === entry.id
      const esPrueba = ['prueba', 're_prueba'].includes(entry.tipo)
      return <div className="am-detail mb-2" key={entry.id}>
        <div className="d-flex justify-content-between align-items-start" style={{ cursor: 'pointer' }} onClick={() => setExpandido(isOpen ? null : entry.id)}>
          <div>
            <span className="am-badge am-badge-sin_datos mr-2">{tipoLabel(entry.tipo)}</span>
            <span className={estadoBadgeClass(entry.estado)}>{entry.estado}</span>
            <div className="mt-1">{entry.contenido}</div>
            <small className="text-muted">{entry.fecha}{entry.id_prueba_referencia ? ` · re-prueba de #${entry.id_prueba_referencia}` : ''}</small>
          </div>
          <span>{isOpen ? '▲' : '▼'}</span>
        </div>

        {isOpen && <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e2e8f0' }}>
          {esPrueba && <>
            {entry.campo_dato && <div><small className="text-muted d-block">Dato</small>{entry.campo_dato}</div>}
            {entry.campo_causa && <div className="mt-1"><small className="text-muted d-block">Causa</small>{entry.campo_causa}</div>}
            {entry.campo_resultado_esperado && <div className="mt-1"><small className="text-muted d-block">Resultado esperado</small>{entry.campo_resultado_esperado}</div>}
            {entry.campo_indicador && <div className="mt-1"><small className="text-muted d-block">Indicador</small>{entry.campo_indicador}</div>}
          </>}

          {isMarketing && <div className="mt-2">
            {ESTADOS.filter(s => s !== entry.estado).map(s => <button key={s} className="btn btn-sm btn-outline-secondary mr-1" onClick={() => changeState(entry, s)}>Marcar {s}</button>)}
          </div>}

          {esPrueba && <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Hitos de revisión</h6>
              {isMarketing && <button className="btn btn-sm btn-outline-primary" onClick={() => addHito(entry)}>+ Agregar hito</button>}
            </div>
            {(entry.hitos || []).length === 0 && <small className="text-muted">Sin hitos todavía.</small>}
            {(entry.hitos || []).map(hito => <HitoRow key={hito.id} hito={hito} isMarketing={isMarketing} piezasDisponibles={piezasDisponibles}
              onSaveResult={() => saveHitoResult(hito)} onDelete={() => deleteHito(hito)} onLinkPieces={ids => savePieceLink('hito', hito.id, ids)}
              onVerMetricas={verMetricas} />)}
          </div>}

          <div className="mt-3">
            <h6>Resultado final</h6>
            <div>{entry.campo_resultado || <span className="text-muted">Sin resultado registrado.</span>}</div>
            {entry.campo_fecha_revision && <small className="text-muted">Revisión: {entry.campo_fecha_revision}</small>}
            {isMarketing && <div className="mt-1"><button className="btn btn-sm btn-outline-secondary" onClick={() => saveFinalResult(entry)}>{entry.campo_resultado ? 'Editar resultado' : 'Guardar resultado'}</button></div>}
            <div className="mt-2">
              <small className="text-muted d-block mb-1">Piezas vinculadas al resultado final</small>
              <PieceSelector isMarketing={isMarketing} piezasDisponibles={piezasDisponibles} vinculadas={entry.piezas || []}
                onSave={ids => savePieceLink('resultado_final', entry.id, ids)} onVerMetricas={verMetricas} />
            </div>
          </div>
        </div>}
      </div>
    })}

    {metricas && <div className="am-modal-backdrop" onClick={() => setMetricas(null)}>
      <div className="am-modal-card" onClick={e => e.stopPropagation()}>
        <div className="d-flex justify-content-between"><h5>Métricas de las piezas seleccionadas</h5><button className="close" onClick={() => setMetricas(null)}>&times;</button></div>
        <div className="row mb-3">
          <div className="col-6 col-md-3 mb-2"><div className="am-card"><small>Gasto</small><strong>S/ {Number(metricas.gasto).toFixed(2)}</strong></div></div>
          <div className="col-6 col-md-3 mb-2"><div className="am-card"><small>Leads</small><strong>{metricas.leads}</strong></div></div>
          <div className="col-6 col-md-3 mb-2"><div className="am-card"><small>CPL</small><strong>{metricas.cpl != null ? `S/ ${metricas.cpl}` : '—'}</strong></div></div>
          <div className="col-6 col-md-3 mb-2"><div className="am-card"><small>CPA</small><strong>{metricas.cpa != null ? `S/ ${metricas.cpa}` : '—'}</strong></div></div>
        </div>
        <div>% Trato: {metricas.pct_trato}% · % Respuesta: {metricas.pct_respuesta}%</div>
        <table className="table table-sm mt-3">
          <tbody>
            <tr><td>Contactos (RP1/RP2/RP3)</td><td className="text-right">{metricas.grupos.contactos.total} ({metricas.grupos.contactos.rp1}/{metricas.grupos.contactos.rp2}/{metricas.grupos.contactos.rp3})</td></tr>
            <tr><td>No contactados (NU1/NU2/NU3)</td><td className="text-right">{metricas.grupos.no_contactados.total} ({metricas.grupos.no_contactados.nu1}/{metricas.grupos.no_contactados.nu2}/{metricas.grupos.no_contactados.nu3})</td></tr>
            <tr><td>Por procesar (Sin Clasificar)</td><td className="text-right">{metricas.grupos.por_procesar.total}</td></tr>
            <tr><td>Renuentes (RT1/RT2)</td><td className="text-right">{metricas.grupos.renuentes.total} ({metricas.grupos.renuentes.rt1}/{metricas.grupos.renuentes.rt2})</td></tr>
          </tbody>
        </table>
      </div>
    </div>}
  </div>
}

function PieceSelector({ isMarketing, piezasDisponibles, vinculadas, onSave, onVerMetricas }) {
  const [editing, setEditing] = useState(false)
  const [selected, setSelected] = useState(vinculadas.map(p => p.id_creative))
  useEffect(() => { setSelected(vinculadas.map(p => p.id_creative)) }, [vinculadas])

  if (!editing) {
    return <div>
      {vinculadas.length === 0 ? <small className="text-muted">Ninguna.</small> : vinculadas.map(p => <span key={p.id_creative} className="am-badge am-badge-sin_datos mr-1 mb-1 d-inline-block">{p.nombre}</span>)}
      <div className="mt-1">
        {isMarketing && <button className="btn btn-sm btn-outline-secondary mr-1" onClick={() => setEditing(true)}>Editar piezas</button>}
        {vinculadas.length > 0 && <button className="btn btn-sm btn-outline-primary" onClick={() => onVerMetricas(vinculadas.map(p => p.id_creative))}>Ver métricas</button>}
      </div>
    </div>
  }
  return <div>
    <PieceChips piezas={piezasDisponibles} selected={selected} onToggle={id => setSelected(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id])} />
    <div className="mt-2">
      <button className="btn btn-sm btn-primary mr-1" onClick={() => { onSave(selected); setEditing(false) }}>Guardar</button>
      <button className="btn btn-sm btn-link" onClick={() => { setSelected(vinculadas.map(p => p.id_creative)); setEditing(false) }}>Cancelar</button>
    </div>
  </div>
}

function HitoRow({ hito, isMarketing, piezasDisponibles, onSaveResult, onDelete, onLinkPieces, onVerMetricas }) {
  return <div className="am-detail mt-2" style={{ background: '#f8fafc' }}>
    <div className="d-flex justify-content-between">
      <div><strong>{hito.fecha}</strong>{hito.descripcion && <div>{hito.descripcion}</div>}</div>
      {isMarketing && !hito.resultado && <button className="btn btn-sm btn-outline-danger" onClick={onDelete}>Eliminar</button>}
    </div>
    <div className="mt-1">{hito.resultado ? <div><small className="text-muted d-block">Resultado</small>{hito.resultado}</div> : <span className="text-muted">Sin resultado.</span>}</div>
    {isMarketing && <button className="btn btn-sm btn-outline-secondary mt-1" onClick={onSaveResult}>{hito.resultado ? 'Editar resultado' : 'Guardar resultado'}</button>}
    <div className="mt-2">
      <small className="text-muted d-block mb-1">Piezas de este hito</small>
      <PieceSelector isMarketing={isMarketing} piezasDisponibles={piezasDisponibles} vinculadas={hito.piezas || []} onSave={onLinkPieces} onVerMetricas={onVerMetricas} />
    </div>
  </div>
}

export default BitacoraAgente
